import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { TableStateService } from '@core/services/table-state.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EKpiGoalsStatusBadge } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.interface';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EEventViewMode, TEventFrontendTab } from '@enums/event-tracking.enum';
import { IPlatformInformationApi } from '@interfaces/campaign-analytics-api.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ICreatorCampaignExport, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { TTileContentStatus } from '@shared/components/social-content-tile/social-content-tile.interface';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { NgxFloatUiDirective, NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';
import {
  IDeepLinkContentsEvent,
  IDeepLinkCreatorInfo,
  IDeepLinkKpiGoalsEvent,
  TDeepLinkEvent,
} from '../../campaign-details-deep-link/campaign-details-deep-link.interface';
import { ECampaignDetailsTab, ICampaignDetailsCreator } from '../../campaign-details.interface';
import { CreatorActionsMenuComponent } from '../creator-actions-menu/creator-actions-menu.component';

const STATUS_SORT_ORDER: Record<EAnalyticsStatus, number> = {
  [EAnalyticsStatus.failed]: 0,
  [EAnalyticsStatus.problem]: 1,
  [EAnalyticsStatus.ok]: 2,
  [EAnalyticsStatus.processing]: 3,
};

@Component({
  imports: [
    CreatorActionsMenuComponent,
    DatatableComponent,
    IconBadgeComponent,
    IconComponent,
    NgxFloatUiModule,
    NumberSuffixPipe,
    SocialBadgeComponent,
    TranslateModule,
    MatTooltipModule,
    TextClampTooltipDirective,
  ],
  selector: 'app-campaign-details-creators-table',
  styleUrl: './campaign-details-creators-table.component.scss',
  templateUrl: './campaign-details-creators-table.component.html',
})
export class CampaignDetailsCreatorsTableComponent {
  public readonly creators = input.required<ICampaignDetailsCreator[]>();
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly creatorCampaignExports = input<Map<string, ICreatorCampaignExport>>(new Map());
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly invitedCreatorIds = input<Set<string>>(new Set());

  public readonly deepLinkClicked = output<TDeepLinkEvent>();

  // This component only ever renders for the creators tab's "list" view - a literal constant
  // rather than an input, since that's structural (the parent only instantiates this component
  // when its view mode is list).
  public readonly viewMode = EEventViewMode.list;
  // Briefing events fired from this tab always report the creators tab as current (structural -
  // this component only renders inside the creators tab).
  public readonly currentTab: TEventFrontendTab = toEventFrontendTab(ECampaignDetailsTab.creators);

  public readonly EAnalyticsStatus = EAnalyticsStatus;
  public readonly floatUiPlacementBottom = NgxFloatUiPlacements.BOTTOM;
  public readonly floatUiTriggerNone = NgxFloatUiTriggers.none;

  public readonly selectedCreator = signal<ICampaignDetailsCreator | null>(null);

  @HostBinding('class.has-payout')
  public get hasPayoutClass(): boolean {
    return this.hasBudgetColumn();
  }

  public readonly hasBudgetColumn = computed(() => !!(this.campaign()?.budget && this.campaign()?.currency));

  public readonly campaignCreatorsMap = computed(() => {
    return new Map(this.campaign()?.campaignCreators?.map((cc) => [cc.creatorId, cc]) ?? []);
  });

  public readonly datatableColumns = computed((): TDatatableColumn<ICampaignDetailsCreator, string>[] => {
    const cols: TDatatableColumn<ICampaignDetailsCreator, string>[] = [
      { dataViewStrategy: (r) => r.name, id: 'creator', isSortable: true },
    ];

    if (!this.exportMode()) {
      cols.push(
        {
          dataViewStrategy: (r) => (this.invitedCreatorIds().has(r.id) ? 1 : 0),
          id: 'invited',
          isSortable: true,
        },
        {
          dataViewStrategy: (r) => (this.creatorCampaignExports().get(r.id)?.isActive ? 1 : 0),
          id: 'briefed',
          isSortable: true,
        },
      );
    }

    if (this.hasBudgetColumn()) {
      cols.push({
        dataViewStrategy: (r) => Number(this.campaignCreatorsMap().get(r.id)?.cost ?? 0),
        id: 'payout',
        isSortable: true,
      });
    }

    cols.push(
      { dataViewStrategy: () => '', id: 'platforms', isSortable: false },
      { dataViewStrategy: (r) => STATUS_SORT_ORDER[r.mergedStatus], id: 'status', isSortable: true },
      { dataViewStrategy: () => '', id: 'actions', isSortable: false },
    );

    return cols;
  });

  public readonly skeletonLoaderConfig = computed(
    (): ISkeletonLoaderConfig => ({
      columnGap: '0',
      columns: [
        {
          circleSize: '40px',
          className: this.hasBudgetColumn()
            ? this.exportMode()
              ? 'w-[33%]'
              : 'w-[25%]'
            : this.exportMode()
              ? 'w-[38%]'
              : 'w-[30%]',
          columnPadding: '0 8px',
          hasText: true,
          rowPadding: '8px 0',
          textWidth: '120px',
          type: 'icon',
        },
        ...(!this.exportMode()
          ? [
              { circleSize: '16px', className: 'w-[9%]', type: 'icon' as const },
              { circleSize: '16px', className: 'w-[9%]', type: 'icon' as const },
            ]
          : []),
        ...(this.hasBudgetColumn()
          ? [{ className: this.exportMode() ? 'w-[14%]' : 'w-[12%]', type: 'text' as const, width: '64px' }]
          : []),
        {
          className: this.hasBudgetColumn()
            ? this.exportMode()
              ? 'w-[21%]'
              : 'w-[17%]'
            : this.exportMode()
              ? 'w-[24%]'
              : 'w-[20%]',
          textWidth: '20px',
          type: 'text',
          width: '60px',
        },
        {
          className: this.hasBudgetColumn()
            ? this.exportMode()
              ? 'w-[24%]'
              : 'w-[20%]'
            : this.exportMode()
              ? 'w-[28%]'
              : 'w-[24%]',
          type: 'text',
          width: '80px',
        },
        {
          borderRadius: '100vw',
          circleSize: '24px',
          className:
            this.exportMode() && !this.hasBudgetColumn() ? 'w-[10%] flex justify-start' : 'w-[8%] flex justify-start',
          type: 'icon',
          width: '0',
        },
      ],
      rows: 5,
      showHeaderControls: false,
      showPaginator: true,
      showSearchField: false,
    }),
  );

  private currentPopover: NgxFloatUiDirective | null = null;
  private readonly elementRef = inject(ElementRef);
  private readonly tableStateService = inject(TableStateService);

  public readonly datatableConfig: IDatatableConfiguration<ICampaignDetailsCreator> = {
    isClientSide: true,
    isPaginationEnabled: true,
    isRowClickEnabled: true,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    storageKey: this.tableStateService.buildKey('campaignDetailsCreators', 'datatable'),
  };

  @HostListener('document:click')
  public onDocumentClick(): void {
    if (this.currentPopover) {
      this.currentPopover.hide();
      this.currentPopover = null;
    }
  }

  public onViewCreator(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    WindowRedirectService.openUrlInNewTab(`/creators/${creator.id}`);
  }

  public onRowClick(event: IRowClickEvent<ICampaignDetailsCreator, string>): void {
    // Stop the original click from bubbling to document:click which would immediately close the popover
    event.event?.stopPropagation();
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      `[data-creator-status-cell="${event.row.id}"]`,
    ) as HTMLElement | null;
    el?.click();
  }

  public onStatusCellClick(creator: ICampaignDetailsCreator, ref: NgxFloatUiDirective, event: Event): void {
    event.stopPropagation();

    if (this.currentPopover === ref) {
      ref.hide();
      this.currentPopover = null;

      return;
    }

    if (this.currentPopover) {
      this.currentPopover.hide();
    }

    this.selectedCreator.set(creator);
    this.currentPopover = ref;
    ref.show();
  }

  public getStatusLabel(status: EAnalyticsStatus): string {
    switch (status) {
      case EAnalyticsStatus.ok:
        return 'pages.campaignDetails.creators.filters.allGood';
      case EAnalyticsStatus.problem:
        return 'pages.campaignDetails.creators.filters.attention';
      case EAnalyticsStatus.failed:
        return 'pages.campaignDetails.creators.filters.critical';
      case EAnalyticsStatus.processing:
        return 'pages.campaignDetails.creators.filters.notStarted';
    }
  }

  public getStatusColorClass(status: EAnalyticsStatus): string {
    switch (status) {
      case EAnalyticsStatus.ok:
        return 'text-shikenso-success-base';
      case EAnalyticsStatus.problem:
        return 'text-shikenso-warning-base';
      case EAnalyticsStatus.failed:
        return 'text-shikenso-error-bright';
      case EAnalyticsStatus.processing:
        return 'text-shikenso-purple-base-60';
    }
  }

  public getStatusDotClass(status: EAnalyticsStatus): string {
    switch (status) {
      case EAnalyticsStatus.ok:
        return 'bg-shikenso-success-base';
      case EAnalyticsStatus.problem:
        return 'bg-shikenso-warning-base';
      case EAnalyticsStatus.failed:
        return 'bg-shikenso-error-bright';
      case EAnalyticsStatus.processing:
        return 'bg-shikenso-purple-base-60';
    }
  }

  public getIssueCount(creator: ICampaignDetailsCreator): number {
    const kpi = creator.ctaKpiStatus;

    if (creator.mergedStatus === EAnalyticsStatus.failed) {
      return kpi.currentFailedKpis + kpi.currentFailedContents + kpi.currentMissingContents;
    }

    if (creator.mergedStatus === EAnalyticsStatus.problem) {
      return kpi.currentProblemKpis + kpi.currentProblemContents;
    }

    return 0;
  }

  public hasIssues(creator: ICampaignDetailsCreator): boolean {
    return creator.mergedStatus === EAnalyticsStatus.failed || creator.mergedStatus === EAnalyticsStatus.problem;
  }

  public generateSocialUrl(usedPlatform: IPlatformInformationApi): string {
    return SocialUtilitiesService.generateSocialUrl(usedPlatform.platform, usedPlatform.platformId);
  }

  public getUsedPlatformTracking(usedPlatform: IPlatformInformationApi): string {
    return `${usedPlatform.platformId}-${usedPlatform.platform}`;
  }

  public getPopoverRows(
    creator: ICampaignDetailsCreator,
  ): Array<{ colorClass: string; label: string; count: number; clickFn: () => void }> {
    const kpi = creator.ctaKpiStatus;
    const rows: Array<{ colorClass: string; label: string; count: number; clickFn: () => void }> = [];

    if (kpi.currentMissingContents > 0) {
      rows.push({
        clickFn: () => this.emitDeepLink(creator, 'missingContents'),
        colorClass: 'bg-shikenso-error-bright',
        count: kpi.currentMissingContents,
        label: 'pages.campaignDetails.overview.missingContents',
      });
    }

    if (kpi.currentFailedContents > 0) {
      rows.push({
        clickFn: () => this.emitDeepLink(creator, 'failedContents'),
        colorClass: 'bg-shikenso-error-bright',
        count: kpi.currentFailedContents,
        label: 'pages.campaignDetails.overview.failedContents',
      });
    }

    if (kpi.currentFailedKpis > 0) {
      rows.push({
        clickFn: () => this.emitDeepLink(creator, 'failedKpis'),
        colorClass: 'bg-shikenso-error-bright',
        count: kpi.currentFailedKpis,
        label: 'pages.campaignDetails.overview.goalsUnavailable',
      });
    }

    if (kpi.currentProblemContents > 0) {
      rows.push({
        clickFn: () => this.emitDeepLink(creator, 'problemContents'),
        colorClass: 'bg-shikenso-warning-base',
        count: kpi.currentProblemContents,
        label: 'pages.campaignDetails.overview.problemContents',
      });
    }

    if (kpi.currentProblemKpis > 0) {
      rows.push({
        clickFn: () => this.emitDeepLink(creator, 'problemKpis'),
        colorClass: 'bg-shikenso-warning-base',
        count: kpi.currentProblemKpis,
        label: 'pages.campaignDetails.overview.goalsBelowTarget',
      });
    }

    return rows;
  }

  public onPopoverRowClick(row: { clickFn: () => void }, event: Event): void {
    event.stopPropagation();
    this.currentPopover?.hide();
    this.currentPopover = null;
    row.clickFn();
  }

  public onAllGoodClick(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    this.currentPopover?.hide();
    this.currentPopover = null;
    this.deepLinkClicked.emit({
      contentStatuses: new Set<TTileContentStatus>(['fulfilled']),
      creator: { icon: creator.icon, id: creator.id, name: creator.name },
      matchingTypes: new Set([EMatchingType.matched]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  public onNotStartedClick(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    this.currentPopover?.hide();
    this.currentPopover = null;
    this.deepLinkClicked.emit({
      creator: this.creatorInfo(creator),
      matchingTypes: new Set([EMatchingType.planned]),
      tab: ECampaignDetailsTab.contents,
    } as IDeepLinkContentsEvent);
  }

  public onMissingContentsClick(creator: ICampaignDetailsCreator, event: Event): void {
    event.stopPropagation();
    this.currentPopover?.hide();
    this.currentPopover = null;
    this.emitDeepLink(creator, 'missingContents');
  }

  private creatorInfo(creator: ICampaignDetailsCreator): IDeepLinkCreatorInfo {
    return { icon: creator.icon, id: creator.id, name: creator.name };
  }

  private emitDeepLink(
    creator: ICampaignDetailsCreator,
    type: 'missingContents' | 'failedContents' | 'problemContents' | 'failedKpis' | 'problemKpis',
  ): void {
    switch (type) {
      case 'missingContents':
        this.deepLinkClicked.emit({
          creator: this.creatorInfo(creator),
          matchingTypes: new Set([EMatchingType.missing]),
          tab: ECampaignDetailsTab.contents,
        } as IDeepLinkContentsEvent);
        break;
      case 'failedContents':
        this.deepLinkClicked.emit({
          contentStatuses: new Set<TTileContentStatus>(['notFulfilled']),
          creator: this.creatorInfo(creator),
          matchingTypes: new Set([EMatchingType.matched]),
          tab: ECampaignDetailsTab.contents,
        } as IDeepLinkContentsEvent);
        break;
      case 'problemContents':
        this.deepLinkClicked.emit({
          contentStatuses: new Set<TTileContentStatus>(['partiallyFulfilled']),
          creator: this.creatorInfo(creator),
          matchingTypes: new Set([EMatchingType.matched]),
          tab: ECampaignDetailsTab.contents,
        } as IDeepLinkContentsEvent);
        break;
      case 'failedKpis':
        this.deepLinkClicked.emit({
          creator: this.creatorInfo(creator),
          statuses: new Set([EKpiGoalsStatusBadge.kpiGoalsUnavailable]),
          tab: ECampaignDetailsTab.kpiGoals,
        } as IDeepLinkKpiGoalsEvent);
        break;
      case 'problemKpis':
        this.deepLinkClicked.emit({
          creator: this.creatorInfo(creator),
          statuses: new Set([EKpiGoalsStatusBadge.kpiGoalsBelowTarget]),
          tab: ECampaignDetailsTab.kpiGoals,
        } as IDeepLinkKpiGoalsEvent);
        break;
    }
  }
}
